/**
 * Asset: Redirect modal
 * ------------------------------------------------------------------------------
 * Detects the visitor's country (MaxMind geoip2), looks up the matching
 * Meermin store in `Frame.MultiStore`, and asks once whether to switch if
 * the visitor is browsing another regional store. It never redirects on its
 * own: the visitor chooses, the choice is remembered (cookie, a year), and a
 * visitor who arrives through a store link with their cart (?mm_cart=) has
 * already chosen. Switching carries the cart across (store-handoff.liquid).
 */
(() => {
  const COOKIE_DISMISSED = 'redirect_modal';
  const COOKIE_GEO = 'redirect_geolocation';
  const COOKIE_SETTINGS = 'redirect_store_settings';
  const DAYS_REMEMBER = 365;
  const DAYS_GEO = 7;

  const config = window.MeerminGeo || {};
  const stores = (window.Frame && window.Frame.MultiStore) || [];

  // Once per visitor: no prompt after a remembered choice, on arrival from
  // another store with the cart, or on the store that already serves them.
  function shouldPrompt({ remembered, search, suggestedStoreCode, currentStoreCode }) {
    if (remembered) return false;
    if (/[?&]mm_cart=/.test(search || '')) return false;
    if (!suggestedStoreCode || suggestedStoreCode === currentStoreCode) return false;
    return true;
  }
  window.MeerminRedirectModal = { shouldPrompt, DAYS_REMEMBER };

  const dialog = document.getElementById('redirect-modal');
  if (!dialog) return;

  const $ = (sel, root = dialog) => root.querySelector(sel);
  const $$ = (sel, root = dialog) => root.querySelectorAll(sel);
  const choicesView = $('[data-view="choices"]');
  const settingsView = $('[data-view="settings"]');
  const flagEl = $('[data-redirect-flag]');
  const countryNameEls = $$('[data-redirect-country-name]');
  const storeNameEls = $$('[data-redirect-store-name]');
  const continueBtn = $('[data-redirect-continue]');
  const switchBtn = $('[data-redirect-switch]');
  const settingsBtn = $('[data-redirect-settings]');
  const returnBtn = $('[data-redirect-return]');
  const closeBtn = $('[data-redirect-close]');
  const form = $('[data-redirect-form]');
  const countrySelect = $('[data-redirect-country-select]');
  const currencySelect = $('[data-redirect-currency-select]');
  const languageSelect = $('[data-redirect-language-select]');
  const updateBtn = $('[data-redirect-update]');

  // Cookie helpers
  function setCookie(name, value, days) {
    const expires = new Date(Date.now() + days * 864e5).toUTCString();
    document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/`;
  }
  function getCookie(name) {
    const m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
    return m ? decodeURIComponent(m[1]) : null;
  }

  // Submit a native Shopify /localization form so Horizon's locale state
  // (country + language cookies) actually flips. Mirrors the form that
  // {% form 'localization' %} renders — same fields the native header
  // dropdown submits.
  function submitNativeLocalization(countryCode, languageCode) {
    const f = document.createElement('form');
    f.method = 'POST';
    f.action = '/localization';
    f.style.display = 'none';
    const add = (name, value) => {
      const i = document.createElement('input');
      i.type = 'hidden';
      i.name = name;
      i.value = value;
      f.appendChild(i);
    };
    add('_method', 'put');
    add('form_type', 'localization');
    add('utf8', '✓');
    add('country_code', countryCode);
    if (languageCode) add('language_code', languageCode);
    add('return_to', window.location.pathname + window.location.search);
    document.body.appendChild(f);
    f.submit();
  }

  // Match visitor's country to a store
  function getMatchingStore(countryCode) {
    if (!countryCode) return null;
    const code = countryCode.toUpperCase();
    const directMatch = stores.find((s) => s.storeCode === code);
    const supportedMatch = stores.find((s) => s.supportedStores.split(',').map((c) => c.trim()).includes(code));
    return supportedMatch || directMatch || stores.find((s) => s.storeCode === 'ROW') || null;
  }

  // Geolocation lookup via MaxMind. Modal silently no-ops if it fails.
  function fetchGeolocation() {
    return new Promise((resolve, reject) => {
      const cached = getCookie(COOKIE_GEO);
      if (cached) {
        try { return resolve(JSON.parse(cached)); } catch (e) { /* fall through */ }
      }

      if (typeof geoip2 === 'undefined') {
        return reject(new Error('geoip2 not loaded'));
      }

      geoip2.country(
        (resp) => {
          const data = {
            countryCode: resp.country && resp.country.iso_code,
            countryName: resp.country && resp.country.names && resp.country.names.en,
            continentCode: resp.continent && resp.continent.code,
          };
          setCookie(COOKIE_GEO, JSON.stringify(data), DAYS_GEO);
          resolve(data);
        },
        (err) => reject(err)
      );
    });
  }

  // UI: render choices view with detected country + suggested store
  function renderChoices(geo, suggested) {
    countryNameEls.forEach((el) => { el.textContent = geo.countryName || ''; });
    storeNameEls.forEach((el) => { el.textContent = suggested.storeName || ''; });
    if (flagEl && suggested.storeCode) {
      flagEl.className = 'redirect-modal__flag flag-icon flag-icon-' + suggested.storeCode.toLowerCase();
    }
    if (switchBtn) {
      switchBtn.dataset.storeUrl = suggested.storeUrl;
      switchBtn.dataset.storeCode = suggested.storeCode;
    }
  }

  // Country select → reset language + currency to that store's defaults
  function handleCountryChange() {
    const selected = countrySelect.value;
    const store = getMatchingStore(selected);
    if (!store) return;

    const langs = store.supportedLanguages.split(',').map((s) => s.trim());
    const langCodes = store.supportedLanguagesCodes.split(',').map((s) => s.trim());
    languageSelect.innerHTML = langs.map((l, i) => `<option value="${langCodes[i] || l}">${l}</option>`).join('');

    const currencies = store.supportedCurrencies.split(',').map((s) => s.trim());
    currencySelect.innerHTML = currencies.map((c) => {
      const code = c.split(' ')[0];
      return `<option value="${code}">${c}</option>`;
    }).join('');

    if (updateBtn) {
      updateBtn.dataset.storeUrl = store.storeUrl;
    }
  }

  function showView(name) {
    if (choicesView) choicesView.hidden = name !== 'choices';
    if (settingsView) settingsView.hidden = name !== 'settings';
  }

  function remember(choice) {
    setCookie(COOKIE_DISMISSED, choice, DAYS_REMEMBER);
  }

  function dismiss() {
    remember('stay');
    dialog.close();
  }

  // To another store, with the cart (falls back to a plain navigation).
  function goToStore(storeUrl) {
    const url = storeUrl + (config.path || '/');
    if (typeof window.meerminGoToStore === 'function') window.meerminGoToStore(url);
    else window.location.assign(url);
  }

  function loadFlagIconStylesheet() {
    if (document.getElementById('flag-icon-css')) return;
    const link = document.createElement('link');
    link.id = 'flag-icon-css';
    link.rel = 'stylesheet';
    link.href = 'https://cdnjs.cloudflare.com/ajax/libs/flag-icon-css/3.3.0/css/flag-icon.min.css';
    document.head.appendChild(link);
  }

  function openDialog() {
    loadFlagIconStylesheet();
    if (typeof dialog.showModal === 'function') {
      dialog.showModal();
    } else {
      dialog.setAttribute('open', '');
    }
  }

  // Event wiring
  if (continueBtn) continueBtn.addEventListener('click', dismiss);
  if (closeBtn) closeBtn.addEventListener('click', dismiss);
  if (switchBtn) {
    switchBtn.addEventListener('click', () => {
      remember('switch');
      const url = switchBtn.dataset.storeUrl;
      if (url) goToStore(url);
    });
  }
  if (settingsBtn) settingsBtn.addEventListener('click', () => showView('settings'));
  if (returnBtn) returnBtn.addEventListener('click', () => showView('choices'));
  if (countrySelect) countrySelect.addEventListener('change', handleCountryChange);
  if (form) {
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      const loc = countrySelect.value;
      const cur = currencySelect.value;
      const lang = languageSelect.value;
      const store = getMatchingStore(loc);
      if (!store) return;

      setCookie(COOKIE_SETTINGS, JSON.stringify({ loc, cur, lang }), DAYS_REMEMBER);
      remember('settings');

      if (store.storeCode === config.storeCode) {
        // Same store → flip Horizon's localization state via native form.
        // Currency follows country in Markets, so we don't pass it.
        submitNativeLocalization(loc, lang);
      } else {
        // Different regional store → that domain, cart included.
        goToStore(store.storeUrl);
      }
    });
  }

  // Boot
  function init() {
    if (getCookie(COOKIE_DISMISSED)) return;
    if (/[?&]mm_cart=/.test(window.location.search)) {
      // Came from another Meermin store on purpose: that is the choice.
      remember('handoff');
      return;
    }

    fetchGeolocation()
      .then((geo) => {
        const suggested = getMatchingStore(geo.countryCode);
        const prompt = shouldPrompt({
          remembered: Boolean(getCookie(COOKIE_DISMISSED)),
          search: window.location.search,
          suggestedStoreCode: suggested && suggested.storeCode,
          currentStoreCode: config.storeCode,
        });
        if (!prompt) return;

        renderChoices(geo, suggested);
        // Pre-select detected country in settings view
        if (countrySelect) {
          const opt = [...countrySelect.options].find((o) => o.value === geo.countryCode);
          if (opt) countrySelect.selectedIndex = opt.index;
          handleCountryChange();
        }
        showView('choices');
        openDialog();
      })
      .catch(() => { /* geolocation unavailable → silent */ });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
